'use client';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { type NodeProps } from '@xyflow/react';
import { Share2 } from 'lucide-react';
import { NodeFrame, type NodeFrameAction } from './node-frame';
import { PostDiscoverCard } from '@/components/discover/post-discover-card';
import { Lightbox, VideoPreview, Model3DPreview, AudioPreview } from '@/components/content-previews';
import { useWorkspaceItem } from '@/components/workspace/store';
import { shareLink } from '@/lib/share';

export type PostNodeData = { itemId: string };

type PostMeta = Parameters<typeof PostDiscoverCard>[0]['post'];

function PostNodeImpl({ data }: NodeProps) {
  const t = useT();
  const itemId = (data as PostNodeData).itemId;
  const item = useWorkspaceItem(itemId);
  const refId = item?.refId ?? null;
  const cache = (item?.state.cache ?? {}) as Record<string, unknown>;

  // Header double-click brings the post into view at the current zoom, via
  // the canvas's `ws:focus-item` chokepoint. It used to `fitView` onto the
  // node (a zoom); workspace zoom is disabled (2026-09-26).
  const fitWidgetToScreen = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('ws:focus-item', { detail: { itemId } }),
    );
  }, [itemId]);

  const [post, setPost] = useState<PostMeta | null>(() =>
    refId
      ? {
          id: refId,
          title: (cache.title as string) ?? null,
          mediaUrl: (cache.mediaUrl as string) ?? null,
          mediaType: (cache.mediaType as string) ?? null,
          thumbnailUrl: (cache.thumbnailUrl as string) ?? null,
        }
      : null,
  );
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!refId) return;
    fetch(`/api/posts/${refId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !data) return;
        const p: PostMeta = (data?.post ?? data) as PostMeta;
        if (p?.id) setPost(p);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [refId]);

  const hasMedia = !!(post?.mediaType && (post.mediaUrl || post.thumbnailUrl));

  const postTitle =
    post?.title || ((post?.content as string | undefined) ?? '').slice(0, 60) || 'Post';
  const actions: NodeFrameAction[] = useMemo(
    () => [
      {
        id: 'share',
        label: 'Share',
        tone: 'slate' as const,
        icon: Share2,
        onClick: () => {
          if (typeof window === 'undefined' || !refId) return;
          void shareLink({
            title: postTitle,
            url: `${window.location.origin}/p/${refId}`,
          });
        },
      },
    ],
    [refId, postTitle],
  );

  if (!item) return null;

  const title = postTitle;

  return (
    <NodeFrame
      itemId={itemId}
      title={title}
      actions={actions}
      // Maximize → true fullscreen takeover (NodeFrame handles the
      // viewport portal). Pass an empty body so the green dot still
      // renders; passing undefined would hide it entirely.
      onMaximize={refId ? () => {} : undefined}
      // Header double-click focuses the camera on this widget — the
      // gentler "zoom to fit" gesture.
      onFocus={refId ? fitWidgetToScreen : undefined}
      bodyPad={false}
      minWidth={240}
      minHeight={220}
      maxWidth={720}
      maxHeight={900}
    >
      {!post ? (
        <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">
          {t('workspace.node.loadingPost')}
        </div>
      ) : hasMedia ? (
        <PostMediaTile post={post} onOpen={() => setLightbox(true)} />
      ) : (
        <div className="h-full w-full overflow-y-auto p-2">
          <PostDiscoverCard post={post} viewMode="grid" />
        </div>
      )}

      {lightbox && post && (post.mediaUrl || post.thumbnailUrl) && (
        <Lightbox
          items={[
            {
              url: (post.mediaUrl || post.thumbnailUrl) as string,
              type: pickMediaType(post.mediaType),
              filename: title,
              mimeType: pickMimeType(post.mediaType),
              contentId: post.id,
            },
          ]}
          onClose={() => setLightbox(false)}
        />
      )}
    </NodeFrame>
  );
}

export const PostNode = memo(PostNodeImpl, (a, b) => {
  return (
    a.id === b.id &&
    a.selected === b.selected &&
    a.dragging === b.dragging
  );
});

/**
 * Edge-to-edge media tile for the canvas. Click anywhere → lightbox; the
 * NodeFrame chrome already exposes "open in new tab" / "share" affordances.
 * We intentionally bypass `PostDiscoverCard` here because that component
 * wraps everything in a `Link` to /p/:id, which prevents an in-place lightbox.
 */
function PostMediaTile({
  post,
  onOpen,
}: {
  post: NonNullable<PostMeta>;
  onOpen: () => void;
}) {
  const mediaSrc = post.mediaUrl || post.thumbnailUrl || '';
  const type = post.mediaType;

  if (type === 'video' && post.mediaUrl) {
    return (
      <div className="h-full w-full bg-black">
        <VideoPreview src={post.mediaUrl} className="h-full w-full" onClick={onOpen} />
      </div>
    );
  }
  if (type === '3d' && post.mediaUrl) {
    return (
      <div className="h-full w-full">
        <Model3DPreview src={post.mediaUrl} className="h-full" onClick={onOpen} />
      </div>
    );
  }
  if (type === 'audio' && post.mediaUrl) {
    return (
      <div className="h-full w-full p-3 flex items-center justify-center">
        <AudioPreview src={post.mediaUrl} />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={post.title || 'Open media'}
      className="block h-full w-full bg-muted/30 group relative"
      style={{
        backgroundImage: mediaSrc ? `url(${JSON.stringify(mediaSrc)})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <span className="absolute inset-0 group-hover:bg-black/10 transition-colors" />
    </button>
  );
}

function pickMediaType(t?: string | null): 'image' | 'video' | 'audio' | 'model3d' | 'document' {
  switch (t) {
    case 'video': return 'video';
    case 'audio': return 'audio';
    case '3d': return 'model3d';
    case 'image': return 'image';
    default: return 'document';
  }
}

function pickMimeType(t?: string | null): string {
  switch (t) {
    case 'video': return 'video/mp4';
    case 'audio': return 'audio/mpeg';
    case '3d': return 'model/gltf-binary';
    case 'image': return 'image/jpeg';
    default: return 'application/octet-stream';
  }
}
